import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setModalsData,
  setReportVisibleColumns,
  TModalsState,
} from "../api/store/commonSlice";
import svgIcons from "../assets/svg/svgIcons";
import { useLocalStorage } from "../utils/localStorageItems";
import { AdminFormFieldSubmit } from "./admin/formFields";
import CheckableListItem from "./checkableListItem";
import FTModal from "./common/FTModal";

type TModalKey = "showEditColumns" | "showEditColumnsDashboard";

interface EditListingColumnsButtonProps {
  additionalClasses?: string;
  disabled?: boolean;
  positioningClasses?: string;
  roundedSize?: string;
  showAlernateIcon?: boolean;
  sizeShapeClasses?: string;
  modalType?: TModalKey;
}

export const EditListingColumnsButton = ({
  additionalClasses = "",
  disabled = false,
  positioningClasses = "absolute top-5 right-5",
  roundedSize = "full",
  showAlernateIcon = true, // deprecated, made always true for consistency
  sizeShapeClasses = "p-2",
  modalType = "showEditColumns",
}: EditListingColumnsButtonProps) => {
  const { t } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const [showText, setShowText] = React.useState(false);

  if (showAlernateIcon) {
    return (
      <button
        className={`${positioningClasses} flex items-center justify-center hover:flex-row ${showText ? `${roundedSize === "full" ? "rounded-e-full" : "rounded-e-lg"} z-listing-table-header-icons-showtext` : `rounded-${roundedSize} z-listing-table-header-icons`} flex-wrap ${sizeShapeClasses} bg-accent-blue-pale ${disabled ? "opacity-50 grayscale pointer-events-none touch-none" : ""} ${additionalClasses} group group-focus:block`}
        type="button"
        onClick={() => {
          dispatch(setModalsData({ ...modalsState, [modalType]: true }));
        }}
        onMouseEnter={() => setShowText(true)}
        onMouseLeave={() => setShowText(false)}
      >
        {showText && (
          <span
            className={`absolute h-full right-full top-0 text-sm font-semibold ${roundedSize === "full" ? "rounded-s-full" : "rounded-s-lg"} ps-4 flex items-center leading-6 bg-accent-blue-pale whitespace-nowrap`}
          >
            {t("admins.edit_columns.show_hide_button_label")}
          </span>
        )}
        <img
          src={svgIcons.HideColumnIcon}
          alt="hide"
          className="align-middle translate-y-[1px]"
        />
      </button>
    );
  } else {
    <button
      className={`absolute top-5 right-5 z-listing-table-header-icons flex hover:flex-row rounded-lg p-2 bg-accent-blue-pale ${additionalClasses}`}
      type="button"
      onClick={() => {
        dispatch(setModalsData({ ...modalsState, [modalType]: true }));
      }}
    >
      <img src={svgIcons.EditIcon} alt="edit" />
    </button>;
  }

  return (
    <button
      className={`${positioningClasses} flex items-center justify-center hover:flex-row ${showText ? `${roundedSize === "full" ? "rounded-e-full" : "rounded-e-lg"} z-listing-table-header-icons-showtext` : `rounded-${roundedSize} z-listing-table-header-icons`} flex-wrap ${sizeShapeClasses} bg-accent-blue-pale ${disabled ? "opacity-50 grayscale pointer-events-none touch-none" : ""} ${additionalClasses} group group-focus:block`}
      onClick={() => {
        dispatch(setModalsData({ ...modalsState, [modalType]: true }));
      }}
      onMouseEnter={() => setShowText(true)}
      onMouseLeave={() => setShowText(false)}
    >
      {showText && (
        <span
          className={`absolute h-full right-full top-0 text-sm font-semibold ${roundedSize === "full" ? "rounded-s-full" : "rounded-s-lg"} ps-4 flex items-center leading-6 bg-accent-blue-pale whitespace-nowrap`}
        >
          {t("admins.edit_columns.show_hide_button_label")}
        </span>
      )}
      <img
        src={showAlernateIcon ? svgIcons.HideColumnIcon : svgIcons.EditIcon}
        alt="edit"
      />
    </button>
  );
};

export type TListingColumn = {
  name: string;
  show: boolean;
  disabled?: boolean;
};

type TModalEditListingColumnsProps = {
  columns: TListingColumn[];
  setColumns: (columns: TListingColumn[]) => void;
  lsKey: string;
  modalType?: TModalKey;
};

export const ModalEditListingColumns: FC<TModalEditListingColumnsProps> = ({
  columns,
  setColumns,
  lsKey,
  modalType = "showEditColumns", // Default to the standard one
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.edit_columns",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const { setLocalStorageItem } = useLocalStorage();
  const isVisible = modalsState[modalType];

  const handleSubmit = () => {
    hideModal();
    const selectedColumnsToSave = columns
      .filter((colItem) => colItem.show)
      .map((colItem) => colItem.name);
    // save to local storage
    setLocalStorageItem(lsKey, selectedColumnsToSave);
    // save to redux store
    dispatch(
      setReportVisibleColumns({
        type: lsKey,
        columns: selectedColumnsToSave,
      })
    );
  };

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, [modalType]: false }));
  };

  if (!isVisible) return null;

  return (
    <FTModal
      heading={t("heading")}
      onHide={hideModal}
      visible={!!isVisible}
      containerStyle="mx-auto max-w-[calc(100vw-4rem)] w-[560px]"
    >
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <div className="col-span-6 grid grid-cols-6 gap-2">
          {columns.map((column, index) => (
            <div className="col-span-6" key={index}>
              <CheckableListItem
                id={`edit_col_checkbox_${lsKey}_${index}`}
                title={column.name}
                checked={column.show}
                disabled={!!column.disabled}
                handleChange={(e) => {
                  e.stopPropagation();
                  setColumns(
                    columns.map((col, i) => {
                      if (i === index) {
                        return { ...col, show: !col.show };
                      }
                      return col;
                    })
                  );
                }}
              />
            </div>
          ))}
        </div>

        <AdminFormFieldSubmit
          label={t("submit_button")}
          type="button"
          variant="primary"
          onClick={handleSubmit}
        />

        {/* <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              onClick={hideModal}
            /> */}
      </form>
    </FTModal>
  );
};
